/**
 * Client guild model — the mirror of the server's `guildState` pushes
 * (SocialModel/PartyModel pattern: adopt the whole push, diff for toasts, bump
 * `rev` so panels re-render).
 *
 * The server's `notice` field is the login reconcile speaking: the guild doc
 * is authoritative, so "you were removed while you were away" arrives here,
 * once, as news — never as an error.
 */
import type { GuildMemberWire, GuildMsg, GuildStateMsg } from "@hyperlight/shared";

export class GuildModel {
  /** Bumped on every adopted push — panels compare and re-render. */
  rev = 0;
  name = "";
  tag = "";
  /** Officer-settable message of the day — toasted once, when you land. */
  motd = "";
  role: GuildMemberWire["role"] | null = null;
  members: GuildMemberWire[] = [];
  invite: { from: string; guild: string; tag: string; until: number } | null = null;
  private seq = 0;
  private seeded = false;

  constructor(private handlers: { toast: (line: string) => void }) {}

  /** Stamp the next op seq (monotonic per session — the social idiom). */
  op(m: Omit<GuildMsg, "t" | "seq">): GuildMsg {
    return { t: "guild", seq: ++this.seq, ...m };
  }

  get inGuild(): boolean {
    return this.role !== null;
  }

  get isLeader(): boolean {
    return this.role === "leader";
  }

  /** Leaders and officers may invite + write the MOTD. */
  get isOfficer(): boolean {
    return this.role === "leader" || this.role === "officer";
  }

  /** My own name in the roster (the panel hides self-targeting actions). */
  get self(): string {
    return this.members.find((m) => m.me)?.name ?? "";
  }

  apply(msg: GuildStateMsg, now: number): void {
    const first = !this.seeded;
    if (!first) {
      const was = new Set(this.members.map((m) => m.name));
      const is = new Set(msg.guild?.members.map((m) => m.name) ?? []);
      const mine = this.self;
      if (this.inGuild && !msg.guild) {
        this.handlers.toast(`YOU HAVE LEFT ${this.name.toUpperCase()}`);
      } else if (!this.inGuild && msg.guild) {
        this.handlers.toast(`WELCOME TO ${msg.guild.name.toUpperCase()} [${msg.guild.tag}]`);
      } else if (msg.guild) {
        for (const m of msg.guild.members) if (!was.has(m.name)) this.handlers.toast(`${m.name.toUpperCase()} JOINED THE GUILD`);
        for (const name of was) if (!is.has(name) && name !== mine) this.handlers.toast(`${name.toUpperCase()} LEFT THE GUILD`);
        if (this.role !== msg.guild.role) this.handlers.toast(`YOU ARE NOW A ${msg.guild.role.toUpperCase()}`);
      }
      if (msg.invite && msg.invite.guild !== this.invite?.guild) {
        this.handlers.toast(`GUILD INVITE — ${msg.invite.guild.toUpperCase()} · O TO ANSWER`);
      }
    }
    if (msg.err) this.handlers.toast(msg.err);
    if (msg.notice) this.handlers.toast(msg.notice); // login reconcile news
    // The message of the day greets you once, as you land — and again only
    // when an officer actually rewrites it.
    const motd = msg.guild?.motd ?? "";
    if (motd && motd !== this.motd) this.handlers.toast(`[${msg.guild?.tag}] ${motd}`);
    this.name = msg.guild?.name ?? "";
    this.tag = msg.guild?.tag ?? "";
    this.motd = msg.guild?.motd ?? "";
    this.role = msg.guild?.role ?? null;
    this.members = msg.guild?.members ?? [];
    this.invite = msg.invite ? { ...msg.invite, until: now + msg.invite.ttl } : null;
    this.seeded = true;
    this.rev++;
  }
}
